import { useState, useEffect } from 'react'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { validatePhone, validateEmail } from 'utils/validators'

const isSuperset = (set, subset) => {
  for (const elem of subset) {
    if (!set.has(elem)) {
      return false
    }
  }
  return true
}

const getViewState = (user, currentUser, permissions, children) => {
  if (children) {
    const childrenUserTypes = new Set(children.map(child => child.userType))
    const showUserType = user.id && user.id !== currentUser.id
    const showPhone = !isSuperset(new Set(['bot', 'sales_rep']), childrenUserTypes)
    const phoneRequired = showPhone && !isSuperset(new Set(['driver']), childrenUserTypes)
    const showCellPhone = !isSuperset(new Set(['shipper', 'bot']), childrenUserTypes)
    const cellPhoneRequired =
      showCellPhone && !isSuperset(new Set(['dispatcher']), childrenUserTypes)
    const showEmail = !isSuperset(new Set(['driver']), childrenUserTypes)
    const showCompany =
      !isSuperset(new Set(['admin', 'bot']), childrenUserTypes) &&
      permissions.navShippers &&
      permissions.navCarriers
    const showRole = !isSuperset(new Set(['driver', 'admin', 'contract_admin']), childrenUserTypes)
    return {
      showUserType,
      showPhone,
      phoneRequired,
      showCellPhone,
      cellPhoneRequired,
      showEmail,
      showCompany,
      showRole,
    }
  }
  return undefined
}

const useUser = (userData, dialogOpen, children) => {
  const { userDetails: currentUser, userPermissions: permissions = {} } = useUserDetails()
  const [user, setUser] = useState({})
  const [validation, setValidation] = useState({})
  const viewState = getViewState(user, currentUser, permissions, children)
  useEffect(() => {
    if (dialogOpen) {
      setUser(userData)
      setValidation({ pristine: true })
    } else {
      setUser({})
    }
  }, [userData.id, dialogOpen])

  const validate = (data = user, viewData = viewState) => {
    const validations = {}
    if (!data.firstName) validations.firstName = 'Required'
    if (!data.lastName) validations.lastName = 'Required'
    if (viewData.showPhone && viewData.phoneRequired) {
      if (!data.phone) validations.phone = 'Required'
      else if (!validatePhone(data.phone)) validations.phone = 'Phone is not valid'
    }
    if (viewData.showCellPhone) {
      if (!data.cellPhone && viewData.cellPhoneRequired) validations.cellPhone = 'Required'
      else if (data.cellPhone && !validatePhone(data.cellPhone))
        validations.cellPhone = 'Cell Phone is not valid'
    }
    if (viewData.showEmail) {
      if (!data.email) validations.email = 'Required'
      else if (!validateEmail(data.email)) validations.email = 'Email is not valid'
    }

    const invalid = Object.keys(validations).length > 0
    setValidation({ invalid, pristine: false, ...validations })
    return !invalid
  }

  const onChange = change =>
    setUser(current => {
      const newState = { ...current, ...change }
      if (!validation.pristine)
        validate(newState, getViewState(newState, currentUser, permissions, children))
      return newState
    })

  return [user, viewState, onChange, validation, validate, children]
}

export default useUser
